import type { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAdminAccess } from "../hooks/useAdminAccess";
import { useSpecificRoleAccess } from "../hooks/useSpecificRoleAccess";

interface AdminProtectedRouteProps {
  children: ReactNode;
  allowTeamLead?: boolean;
}

const AdminProtectedRoute: FC<AdminProtectedRouteProps> = ({ children, allowTeamLead = false }) => {
  const { user, loading } = useAuth();
  const { hasAccess, hasTeamLeadAccess, loading: accessLoading } = useAdminAccess();
  const { hasAccess: isSpecificRole, loading: specificRoleLoading } =
    useSpecificRoleAccess();
  const location = useLocation();

  if (loading || accessLoading || specificRoleLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    // Save the attempted location for redirect after login
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  }

  // If user has role_id 6, redirect them to the location_variant page
  if (isSpecificRole) {
    return <Navigate to="/location_variant" replace />;
  }

  if (!hasAccess && !(allowTeamLead && hasTeamLeadAccess)) {
    // User is authenticated but doesn't have the required access
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;
